import { monthName } from "./format";

interface Props {
  months: string[];
  fromMonth: string;
  toMonth: string;
  onChange: (fromMonth: string, toMonth: string) => void;
}

const RANGE_PRESETS = [
  { label: "6M", monthCount: 6 },
  { label: "1Y", monthCount: 12 },
  { label: "2Y", monthCount: 24 },
];

const HANDLE_CLASSES = [
  "absolute inset-0 w-full h-5 m-0 appearance-none bg-transparent pointer-events-none",
  "[&::-webkit-slider-runnable-track]:bg-transparent [&::-moz-range-track]:bg-transparent",
  "[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none",
  "[&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full",
  "[&::-webkit-slider-thumb]:bg-rs-green [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-rs-black",
  "[&::-webkit-slider-thumb]:cursor-pointer",
  "[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3",
  "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-rs-green [&::-moz-range-thumb]:border-2",
  "[&::-moz-range-thumb]:border-rs-black [&::-moz-range-thumb]:cursor-pointer",
  "focus-visible:outline-none focus-visible:[&::-webkit-slider-thumb]:ring-2 focus-visible:[&::-webkit-slider-thumb]:ring-rs-off-white",
].join(" ");

function presetButtonClasses(isActive: boolean): string {
  return `bg-transparent border rounded-full px-2.5 py-0.5 text-xs cursor-pointer ${
    isActive
      ? "border-rs-green text-rs-green"
      : "border-rs-jet text-rs-light-gray hover:border-rs-green hover:text-rs-green"
  }`;
}

export function MonthRangeSlider({ months, fromMonth, toMonth, onChange }: Props) {
  if (months.length < 2) return null;
  const lastIndex = months.length - 1;
  const fromIndex = Math.max(months.indexOf(fromMonth), 0);
  const toIndex = Math.max(months.indexOf(toMonth), 0);
  const percentFor = (index: number) => (index / lastIndex) * 100;
  const selectedMonthCount = toIndex - fromIndex + 1;
  const presets = RANGE_PRESETS.filter((preset) => preset.monthCount < months.length);
  const applyPreset = (monthCount: number) => onChange(months[Math.max(lastIndex - monthCount + 1, 0)], months[lastIndex]);
  const isPresetActive = (monthCount: number) => toIndex === lastIndex && selectedMonthCount === monthCount;

  return (
    <div className="flex flex-col gap-2 mb-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-rs-light-gray">
        <span>
          Showing <strong className="text-rs-off-white">{monthName(fromMonth)}</strong> to{" "}
          <strong className="text-rs-off-white">{monthName(toMonth)}</strong> · {selectedMonthCount}{" "}
          {selectedMonthCount === 1 ? "month" : "months"}
        </span>
        <div className="flex gap-1.5" role="group" aria-label="Quick ranges">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className={presetButtonClasses(isPresetActive(preset.monthCount))}
              aria-pressed={isPresetActive(preset.monthCount)}
              onClick={() => applyPreset(preset.monthCount)}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            className={presetButtonClasses(fromIndex === 0 && toIndex === lastIndex)}
            aria-pressed={fromIndex === 0 && toIndex === lastIndex}
            onClick={() => onChange(months[0], months[lastIndex])}
          >
            All
          </button>
        </div>
      </div>

      <div className="relative h-5">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-rs-jet" aria-hidden="true" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-rs-green"
          style={{ left: `${percentFor(fromIndex)}%`, right: `${100 - percentFor(toIndex)}%` }}
          aria-hidden="true"
        />
        <input
          type="range"
          min={0}
          max={lastIndex}
          step={1}
          value={fromIndex}
          aria-label="From month"
          aria-valuetext={monthName(fromMonth)}
          className={HANDLE_CLASSES}
          style={{ zIndex: fromIndex === lastIndex ? 2 : 1 }}
          onChange={(event) => onChange(months[Math.min(Number(event.target.value), toIndex)], toMonth)}
        />
        <input
          type="range"
          min={0}
          max={lastIndex}
          step={1}
          value={toIndex}
          aria-label="To month"
          aria-valuetext={monthName(toMonth)}
          className={HANDLE_CLASSES}
          style={{ zIndex: 1 }}
          onChange={(event) => onChange(fromMonth, months[Math.max(Number(event.target.value), fromIndex)])}
        />
      </div>

      <div className="flex justify-between text-xs text-rs-light-gray">
        <span>{monthName(months[0])}</span>
        <span>{monthName(months[lastIndex])}</span>
      </div>
    </div>
  );
}
